import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { Button } from '@optools/components/ui/button';
import { PipelineStatus, Region } from '@optools/services/operator-gateway/types/generated/graphql';

import { usePipelineControls } from '../context/pipeline-controls-context';

export interface PipelineControlButtonProps {
  gamePk: number;
  mediaId: string;
  navigateOnDeprovision?: boolean;
  pipelineId: number;
  region?: Region;
  status: PipelineStatus;
}

export function PipelineControlButton({
  gamePk,
  mediaId,
  navigateOnDeprovision,
  pipelineId,
  region,
  status,
}: PipelineControlButtonProps) {
  const {
    archiveMedia,
    archiveMediaLoading,
    startMedia,
    startMediaLoading,
    stopMedia,
    stopMediaLoading,
    unscheduleMedia,
    unscheduleMediaLoading,
  } = usePipelineControls();

  return [
    PipelineStatus.PROVISIONED,
    PipelineStatus.STARTING,
    PipelineStatus.SCHEDULED,
    PipelineStatus.RUNNING,
    PipelineStatus.ENDING,
    PipelineStatus.ENDED,
  ].includes(status) ? (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        {[PipelineStatus.PROVISIONED, PipelineStatus.STARTING, PipelineStatus.SCHEDULED].includes(
          status,
        ) ? (
          <ConfirmationDialog
            description="Are you sure you want to deprovision this pipeline?"
            title="Deprovision Pipeline"
            onConfirm={() =>
              unscheduleMedia({
                gamePk,
                mediaId,
                navigateOnSuccess: navigateOnDeprovision,
                pipelineId,
                region,
              })
            }>
            <Button
              aria-label="Deprovision"
              className="w-30"
              disabled={unscheduleMediaLoading || status === PipelineStatus.STARTING}
              loading={unscheduleMediaLoading}
              variant="outline">
              Deprovision
            </Button>
          </ConfirmationDialog>
        ) : null}

        {/* Start Stream */}
        {[PipelineStatus.PROVISIONED, PipelineStatus.STARTING].includes(status) ? (
          <ConfirmationDialog
            description="Are you sure you want to start the stream?"
            title="Start Stream"
            onConfirm={() => startMedia({ gamePk, mediaId, pipelineId, region })}>
            <Button
              aria-label="Start Stream"
              className="w-30"
              disabled={startMediaLoading || status === PipelineStatus.STARTING}
              loading={startMediaLoading || status === PipelineStatus.STARTING}>
              Start Stream
            </Button>
          </ConfirmationDialog>
        ) : null}

        {/* End Stream */}
        {[PipelineStatus.RUNNING, PipelineStatus.ENDING].includes(status) ? (
          <ConfirmationDialog
            description="Are you sure you want to end the stream?"
            title="End Stream"
            onConfirm={() => stopMedia({ gamePk, mediaId, pipelineId, region })}>
            <Button
              aria-label="End Stream"
              className="w-30"
              disabled={stopMediaLoading || status === PipelineStatus.ENDING}
              loading={stopMediaLoading || status === PipelineStatus.ENDING}
              variant="destructive">
              End Stream
            </Button>
          </ConfirmationDialog>
        ) : null}

        {/* Archive Stream */}
        {[PipelineStatus.ENDED].includes(status) ? (
          <ConfirmationDialog
            description="Are you sure you want to archive the stream?"
            title="Archive Stream"
            onConfirm={() => archiveMedia({ gamePk, mediaId, pipelineId, region })}>
            <Button
              aria-label="Archive Stream"
              className="w-30"
              disabled={archiveMediaLoading}
              loading={archiveMediaLoading}
              variant="secondary">
              Archive Stream
            </Button>
          </ConfirmationDialog>
        ) : null}
      </div>
    </div>
  ) : null;
}
